import ListIcon from '@mui/icons-material/List';
import { Search } from '@mui/icons-material';
import { Box, InputAdornment, List, ListItem, ListItemText, Typography } from '@mui/material';
import { getNodeColorByGroup, getNodeConfig, NodeColumnsInfo } from 'api/workflows-steps/formSchema';
import InfoDialog from 'components/dialog/InfoDialog.tsx';
import useDialog from 'hooks/useDialog';
import { PillIconButton } from 'components/button/PillIconButton';
import { ChangeEvent, useMemo, useState } from 'react';
import { ColumnGroupHeader, ColumnGroupIcon, ColumnListButton, ColumnSearchField } from './ColumnSelectorButton.styles';

type ColumnSelectorButtonProps = {
  columns: NodeColumnsInfo[];
  onSelect: (columnName: string) => void;
  disabled?: boolean;
  className?: string;
  title?: string;
  ariaLabel?: string;
  size?: 'small' | 'medium' | 'large';
};

const SEARCH_THRESHOLD = 6;

const ColumnSelectorButton = ({
  columns,
  onSelect,
  disabled = false,
  className,
  title = 'Select column',
  ariaLabel = 'select column',
  size = 'small',
}: ColumnSelectorButtonProps) => {
  const [showInfoDialog, SelectInfoDialog] = useDialog(InfoDialog);
  const [search, setSearch] = useState('');

  const totalColumns = useMemo(() => columns.reduce((n, c) => n + c.outputColumns.length, 0), [columns]);
  const showSearch = totalColumns > SEARCH_THRESHOLD;

  const filteredGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return columns;
    return columns
      .map((col) => {
        const groupMatches = col.inputStepName?.toLowerCase().includes(q) || col.fileName?.toLowerCase().includes(q);
        const outputColumns = groupMatches
          ? col.outputColumns
          : col.outputColumns.filter((c) => c.toLowerCase().includes(q));
        return { ...col, outputColumns };
      })
      .filter((col) => col.outputColumns.length > 0);
  }, [columns, search]);

  if (columns.length === 0) {
    return null;
  }

  const handleSelect = (columnName: string) => {
    onSelect(columnName);
    showInfoDialog(false);
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value ?? '');

  const openDialog = () => {
    setSearch('');
    showInfoDialog(true);
  };

  return (
    <>
      <PillIconButton
        className={className}
        aria-label={ariaLabel}
        size={size}
        title={title}
        onClick={openDialog}
        disabled={disabled}
      >
        <ListIcon fontSize="inherit" />
      </PillIconButton>
      <SelectInfoDialog title={title} fullWidth maxWidth="sm" buttonsSettings={{ confirmText: 'Close' }}>
        {showSearch && (
          <ColumnSearchField
            placeholder="Filter columns"
            type="search"
            value={search}
            onChange={handleSearch}
            fullWidth
            size="small"
            autoComplete="off"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        )}
        <Box sx={{ maxHeight: '50vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filteredGroups.length === 0 ? (
            <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
              No columns match your search.
            </Typography>
          ) : (
            filteredGroups.map((col) => {
              const nodeConfig = col.stepType ? getNodeConfig(col.stepType) : undefined;
              return (
                <div key={col.id}>
                  <ColumnGroupHeader>
                    {nodeConfig?.icon && (
                      <ColumnGroupIcon nodeColor={getNodeColorByGroup(nodeConfig.group)}>
                        {nodeConfig.icon}
                      </ColumnGroupIcon>
                    )}
                    <div className="flex flex-col min-w-0">
                      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem' }}>{col.inputStepName}</Typography>
                      {col.fileName && (
                        <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                          {col.fileName}
                        </Typography>
                      )}
                    </div>
                  </ColumnGroupHeader>
                  <List disablePadding>
                    {col.outputColumns.map((columnName) => (
                      <ListItem key={columnName} component="div" disablePadding>
                        <ColumnListButton onClick={() => handleSelect(columnName)}>
                          <ListItemText primary={columnName} />
                        </ColumnListButton>
                      </ListItem>
                    ))}
                  </List>
                </div>
              );
            })
          )}
        </Box>
      </SelectInfoDialog>
    </>
  );
};

export default ColumnSelectorButton;
